import * as I from "core/api/coreApiClient";
import type { PageSectionState } from "core/redux/reducers/page/pageReducer.types";
import { ThemeVars } from "core/utilities/theme";

/**
 * Sections containing PropertiesListing, EntityHierarchy or LightTable have unknown content when collapsed.
 * Sections containing Table returns the count when collapsed, so we can evaluate them early on.
 * Undefined means that is can't properly get evaluated as the section is still collapsed.
 */
export function isSectionDataEmpty(section: I.Section): boolean | undefined {
  switch (section.type) {
    case I.SectionTypeConstants.Dynamic:
      return false;
    case I.SectionTypeConstants.PropertiesListing:
    case I.SectionTypeConstants.Tracking:
      if (section.expanded) {
        return Boolean(section.propertyValues && section.propertyValues.length === 0);
      }
      return undefined;
    case I.SectionTypeConstants.LightTable:
      if (section.expanded) {
        return !!(section.lightTable && section.lightTable.noDataAvailableLabel);
      }
      return undefined;
    case I.SectionTypeConstants.Table:
      return Boolean(section.table && section.table.rowData && section.table.rowData.countAll === 0);
    case I.SectionTypeConstants.Tabs:
      return false;
    case I.SectionTypeConstants.EntityHierarchy:
      if (section.expanded) {
        return Boolean(section.entityHierarchy && section.entityHierarchy.nodes.length === 0);
      }
      return undefined;
    default:
      return false;
  }
}

export function isSectionMarkedAsEmpty(sectionStatus: PageSectionState, subSection: boolean) {
  const { empty } = sectionStatus;
  if (sectionStatus.toggledVisibility || subSection || empty === undefined) {
    return false;
  }
  return empty;
}

export function getSectionById(sections: I.Section[], sectionId: string) {
  return sections.find((section) => section.id === sectionId);
}

export function getSectionsExceptIds(sections: I.Section[], sectionsIds: string[]) {
  return sections.filter((section) => !sectionsIds.includes(section.id));
}

export function updateSectionSections(section: I.Section, updatedSection: I.Section): I.Section {
  return {
    ...section,
    sections: section.sections.map((s) => (s.id === updatedSection.id ? updatedSection : s)),
  };
}

export function getSectionActiveRowSelection(section: I.Section) {
  const { table } = section;

  return table &&
    table.rowState &&
    table.rowState.rowSelectionSettings &&
    table.rowState.rowSelectionSettings.activeRowSelection
    ? table.rowState.rowSelectionSettings.activeRowSelection
    : undefined;
}

/**
 * Return the maximum height the content of a section can have on the current viewport size
 */
export function sectionContentMaxHeight(viewportHeight: number, pageMarginTop: number, headless: boolean) {
  const spacing = ThemeVars.spacing * 2;

  const sectionHeaderHeight = headless ? spacing : spacing + 25 + spacing;
  const sectionFooterHeight = spacing;
  const pageSectionMargins = spacing * 2;

  return viewportHeight - pageMarginTop - pageSectionMargins - sectionHeaderHeight - sectionFooterHeight;
}

/**
 * Return the maximum width the content of a section can have on the current viewport size
 */
export function sectionContentMaxWidth(viewportWidth: number) {
  const spacing = ThemeVars.spacing * 2;

  const sectionPadding = spacing * 2;
  const pageSectionMargins = spacing * 2;

  return viewportWidth - sectionPadding - pageSectionMargins;
}
